import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import AgentService from '../../services/api/agents';
import { API_RESPONSE } from '../../settings/shared/constants';
import AutocompleteInput from './AutocompleteInput';
import '../../assets/css/agentapps.css';

const DEFAULT_HEADER_KEY_SUGGESTIONS = [
  'Content-Type',
  'Authorization',
  'Accept',
  'User',
  'Agent',
  'Cache-Control',
  'Host',
  'Origin',
  'Referer',
  'Cookie',
  'x-api-key'
];

const DEFAULT_HEADER_VALUE_SUGGESTIONS_BY_KEY = {
  'content-type': [
    'application/json',
    'application/xml',
    'text/plain',
    'multipart/form-data',
    'application/x-www-form-urlencoded'
  ],
  accept: ['application/json', '*/*', 'text/html', 'application/xml'],
  authorization: ['Bearer <token>', 'Basic <base64>', 'ApiKey <key>'],
  'accept-encoding': ['gzip', 'deflate', 'br'],
  'accept-language': ['en-US', 'en-GB', 'fr-FR'],
  'cache-control': ['no-cache', 'no-store', 'max-age=0'],
  connection: ['keep-alive', 'close'],
  'access-control-allow-origin': ['*'],
  'content-encoding': ['gzip', 'compress', 'br'],
  'transfer-encoding': ['chunked']
};

const OAUTH_PROVIDERS = ['slack', 'linkedin', 'salesforce', 'microsoft-teams'];

function normalizeHeaderKeyForLookup(key) {
  return String(key || '').trim().toLowerCase();
}

function splitUrlAndQuery(rawUrl) {
  const url = String(rawUrl || '');
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return { baseUrl: url, queryString: '' };
  return { baseUrl: url.slice(0, qIndex), queryString: url.slice(qIndex + 1) };
}

function queryParamsArrayFromQueryString(queryString) {
  const qs = String(queryString || '').replace(/^\?/, '');
  if (!qs) return [];
  const usp = new URLSearchParams(qs);
  const pairs = [];
  for (const [key, value] of usp.entries()) {
    pairs.push({ key, value });
  }
  return pairs;
}

function buildUrlFromBaseAndParams(baseUrl, paramsArray) {
  const base = String(baseUrl || '');
  const arr = Array.isArray(paramsArray) ? paramsArray : [];
  const usp = new URLSearchParams();
  arr.forEach((p) => {
    const k = String(p?.key || '').trim();
    if (!k) return;
    usp.append(k, String(p?.value ?? ''));
  });
  const qs = usp.toString();
  if (!qs) return base;
  return `${base}?${qs}`;
}

function hasAnyKeyValueData(pairs) {
  if (!Array.isArray(pairs)) return false;
  return pairs.some((p) => {
    const k = String(p?.key || '').trim();
    const v = String(p?.value || '').trim();
    return Boolean(k || v);
  });
}

/**
 * Generic visibility check for any field with a `showWhen` clause.
 * showWhen: { field: <name of another field>, values: [<allowed values>] }
 */
function isFieldVisible(field, values) {
  if (!field?.showWhen) return true;
  const { field: dependsOnField, values: allowedValues } = field.showWhen;
  const currentValue = values?.[dependsOnField];
  return Array.isArray(allowedValues) ? allowedValues.includes(currentValue) : true;
}

/**
 * Resets any field whose `showWhen.field` points at `changedFieldName`
 * and is no longer visible after the change.
 */
function clearNowHiddenDependents(changedFieldName, nextValues, allFields) {
  const cleared = { ...nextValues };
  (allFields || []).forEach((f) => {
    if (f?.showWhen?.field === changedFieldName) {
      const stillVisible = isFieldVisible(f, nextValues);
      if (!stillVisible) {
        cleared[f.name] = f.type === 'keyvalue' ? [{ key: '', value: '' }] : '';
      }
    }
  });
  return cleared;
}

function pairValueToString(vRaw) {
  if (typeof vRaw === 'string') return vRaw.trim();
  if (vRaw == null) return '';
  return String(vRaw).trim();
}

/** Validate key/value rows; ignore fully blank rows. */
function validateKeyValueField(field, value) {
  const pairs = Array.isArray(value) ? value : [];
  const nonEmptyPairs = pairs.filter((pair) => {
    const k = typeof pair?.key === 'string' ? pair.key.trim() : '';
    const v = pairValueToString(pair?.value);
    return Boolean(k || v);
  });

  if (field.required && nonEmptyPairs.length === 0) {
    return `${field.label} is required`;
  }

  for (const pair of nonEmptyPairs) {
    const k = typeof pair?.key === 'string' ? pair.key.trim() : '';
    const v = pairValueToString(pair?.value);
    if (!k && v) {
      return 'Key is required for all entries';
    }
  }
  return '';
}

function parseRecordInfo(record) {
  const raw = record?.info ?? record?.configuration ?? record?.additionalInfo ?? {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return {};
    }
  }
  return raw && typeof raw === 'object' ? raw : {};
}

// Saved Kafka rows may use the backend JSON name or an older form key.
const INFO_FIELD_ALIASES = {
  bootstrap_servers: ['bootstrapServers'],
  topic_name: ['topicName'],
  consumer_group_id: ['consumerGroupId'],
  security_protocol: ['securityProtocol'],
  saslUsername: ['username'],
  saslPassword: ['password']
};

function readInfoField(info, fieldName) {
  const sources = [info, info?.additionalFields];
  const keys = [fieldName, ...(INFO_FIELD_ALIASES[fieldName] || [])];
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = source[key];
      if (value != null && value !== '') return value;
    }
  }
  return undefined;
}

function objectToKeyValueRows(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return [{ key: '', value: '' }];
  }
  const rows = Object.entries(obj)
      .filter(([key]) => String(key).trim())
      .map(([key, value]) => ({
        key,
        value:
            value == null
                ? ''
                : typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value)
      }));
  return rows.length ? rows : [{ key: '', value: '' }];
}

function keyValueRowsToObject(rows) {
  const obj = {};
  (Array.isArray(rows) ? rows : []).forEach((pair) => {
    if (pair?.key?.trim()) {
      obj[pair.key] = pair.value || '';
    }
  });
  return obj;
}

function buildFormDataFromConnectedApp(record, configSchema) {
  const info = parseRecordInfo(record);
  const initialData = {};
  const initialModes = {};
  const fields = Array.isArray(configSchema?.fields) ? configSchema.fields : [];

  fields.forEach((field) => {
    if (field.type === 'keyvalue') {
      initialData[field.name] = objectToKeyValueRows(info[field.name]);
    } else if (field.type === 'payload') {
      const rawPayload = info[field.name];
      if (rawPayload && typeof rawPayload === 'object' && !Array.isArray(rawPayload)) {
        initialData[field.name] = objectToKeyValueRows(rawPayload);
        initialModes[field.name] = 'keyvalue';
      } else if (rawPayload != null) {
        initialData[field.name] =
            typeof rawPayload === 'string'
                ? rawPayload
                : JSON.stringify(rawPayload, null, 2);
        initialModes[field.name] = 'json';
      } else {
        initialData[field.name] = '';
        initialModes[field.name] = 'json';
      }
    } else if (
        field.name === 'method' &&
        (field.type === 'select' || field.type === 'dropdown')
    ) {
      initialData[field.name] =
          info[field.name] ||
          field.defaultValue ||
          field.options?.[0]?.value ||
          'GET';
    } else {
      const saved = readInfoField(info, field.name);
      initialData[field.name] =
          saved != null
              ? String(saved)
              : field.defaultValue || '';
    }
  });

  if (record?.name != null) {
    initialData.name = String(record.name);
  }

  return { initialData, initialModes };
}

function buildEmptyFormDataFromSchema(configSchema) {
  const initialData = {};
  const initialModes = {};
  const fields = Array.isArray(configSchema?.fields) ? configSchema.fields : [];

  fields.forEach((field) => {
    if (field.type === 'keyvalue') {
      initialData[field.name] =
          field.defaultValue &&
          Array.isArray(field.defaultValue) &&
          field.defaultValue.length > 0
              ? field.defaultValue
              : [{ key: '', value: '' }];
    } else if (field.type === 'payload') {
      initialData[field.name] = field.defaultValue || '';
      initialModes[field.name] = 'json';
    } else if (
        field.name === 'method' &&
        (field.type === 'select' || field.type === 'dropdown')
    ) {
      initialData[field.name] =
          field.defaultValue || field.options?.[0]?.value || 'GET';
    } else {
      initialData[field.name] = field.defaultValue || '';
    }
  });

  return { initialData, initialModes };
}

// ---------------------------------------------------------------------------
// Shared style bits
// ---------------------------------------------------------------------------

const spinnerStyle = {
  width: '16px',
  height: '16px',
  border: '2px solid #ffffff',
  borderTop: '2px solid transparent',
  borderRadius: '50%',
  display: 'inline-block'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
  color: '#374151'
};

const errorTextStyle = { color: '#ef4444', fontSize: '12px', marginTop: '4px', marginBottom: 0 };
const helpTextStyle = { color: '#6b7280', fontSize: '12px', marginTop: '4px', marginBottom: 0 };

const kvMiniLabelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const kvInputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '14px',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  backgroundColor: '#ffffff'
};

const kvFocus = (e) => {
  e.target.style.borderColor = '#3b82f6';
  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
};
const kvBlur = (e) => {
  e.target.style.borderColor = '#d1d5db';
  e.target.style.boxShadow = 'none';
};

const focusFirstEmptyKey = (fieldName) => {
  setTimeout(() => {
    const container = document.querySelector(`[data-field-name="${fieldName}"]`);
    if (!container) return;
    const emptyKey = Array.from(container.querySelectorAll('input[placeholder="Enter key"]')).find(
        (input) => !input.value
    );
    if (emptyKey) emptyKey.focus();
  }, 10);
};

/**
 * Key/value editor used by both `keyvalue` fields and `payload` fields in key-value mode.
 */
const KeyValueBox = ({
                       fieldName,
                       hasError,
                       pairs,
                       onChange,
                       tableLayout,
                       autocomplete,
                       keySuggestions,
                       getValueSuggestions
                     }) => {
  const rows = pairs.length === 0 ? [{ key: '', value: '' }] : pairs;

  const updateRow = (index, patch) => {
    const next = [...rows];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };

  const addRow = () => {
    onChange([...rows, { key: '', value: '' }]);
    focusFirstEmptyKey(fieldName);
  };

  const removeRow = (index) => {
    let next = rows.filter((_, i) => i !== index);
    if (next.length === 0) next = [{ key: '', value: '' }];
    onChange(next);
  };

  const onEnterAddRow = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRow();
    }
  };

  const renderInput = (kind, pair, index) => {
    const isKey = kind === 'key';
    const current = isKey ? pair.key || '' : pair.value || '';
    const placeholder = isKey ? 'Enter key' : 'Enter value';
    const setValue = (next) => updateRow(index, { [kind]: next });

    if (autocomplete) {
      return (
          <AutocompleteInput
              value={current}
              onChange={setValue}
              options={isKey ? keySuggestions : getValueSuggestions(pair.key)}
              placeholder={placeholder}
              inputProps={{
                type: 'text',
                style: kvInputStyle,
                onFocus: kvFocus,
                onBlur: kvBlur,
                onKeyDown: onEnterAddRow
              }}
          />
      );
    }

    return (
        <input
            type="text"
            value={current}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onEnterAddRow}
            placeholder={placeholder}
            style={kvInputStyle}
            onFocus={kvFocus}
            onBlur={kvBlur}
        />
    );
  };

  return (
      <div
          data-field-name={fieldName}
          style={{
            border: `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`,
            borderRadius: '6px',
            padding: '8px',
            backgroundColor: '#ffffff',
            transition: 'border-color 0.2s'
          }}
      >
        {tableLayout && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 18px 1fr 52px',
              gap: '10px',
              alignItems: 'center',
              padding: '6px 8px',
              marginBottom: '8px',
              borderBottom: '1px solid #f1f5f9',
              color: '#6b7280',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              <div>Key</div>
              <div />
              <div>Value</div>
              <div />
            </div>
        )}
        <div style={{ marginBottom: '8px' }}>
          {rows.map((pair, index) => (
              <div
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: index < rows.length - 1 ? '5px' : 0,
                    alignItems: 'center',
                    borderRadius: '4px',
                    backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                    transition: 'background-color 0.2s'
                  }}
              >
                <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    {!tableLayout && <label style={kvMiniLabelStyle}>Key</label>}
                    {renderInput('key', pair, index)}
                  </div>
                  <div style={{
                    color: '#d1d5db',
                    fontSize: '18px',
                    paddingTop: tableLayout ? 0 : '24px'
                  }}>
                    <i className="fa fa-arrow-right"></i>
                  </div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    {!tableLayout && <label style={kvMiniLabelStyle}>Value</label>}
                    {renderInput('value', pair, index)}
                  </div>
                </div>
                <button
                    type="button"
                    onClick={() => removeRow(index)}
                    style={{
                      padding: '10px 12px',
                      border: 'none',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      minWidth: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.2s, transform 0.1s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fecaca';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Remove this pair"
                >
                  <i className="fa fa-trash"></i>
                </button>
              </div>
          ))}
        </div>
        <button
            type="button"
            onClick={addRow}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff',
              color: '#6b7280',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
        >
          <i className="fa fa-plus" style={{ fontSize: '12px' }}></i>
          <span>Add</span>
        </button>
      </div>
  );
};

KeyValueBox.propTypes = {
  fieldName: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  pairs: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  tableLayout: PropTypes.bool,
  autocomplete: PropTypes.bool,
  keySuggestions: PropTypes.array,
  getValueSuggestions: PropTypes.func
};

KeyValueBox.defaultProps = {
  hasError: false,
  tableLayout: false,
  autocomplete: false,
  keySuggestions: [],
  getValueSuggestions: () => []
};

/**
 * Generic Agent Configuration Modal
 * Dynamically renders form fields based on JSON configuration schema.
 * Works for both create and edit (pass `editRecord`) for every provider.
 */
const AgentConfigModal = ({ provider, agentName, configSchema, onClose, onSave, editRecord = null }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const [showDraftSaved, setShowDraftSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [payloadModes, setPayloadModes] = useState({}); // fieldName -> 'json' | 'keyvalue'
  const [visiblePasswords, setVisiblePasswords] = useState({}); // fieldName -> boolean
  const [activeAdvancedTab, setActiveAdvancedTab] = useState('params'); // params | headers | payload
  const suppressEndpointParamSyncRef = useRef(false);
  const draftSavedFadeTimerRef = useRef(null);
  const draftSavedHideTimerRef = useRef(null);

  const isEditMode = Boolean(editRecord?.id);

  // Initialize form data from schema (create) or load existing connected app (edit)
  useEffect(() => {
    if (!configSchema?.fields) return undefined;

    let cancelled = false;

    const applyFormState = ({ initialData, initialModes }) => {
      if (cancelled) return;
      setFormData(initialData);
      setPayloadModes(initialModes);
      setErrors({});
      setSaveError(null);
    };

    if (editRecord?.id) {
      (async () => {
        setIsLoadingEdit(true);
        try {
          let record = editRecord;
          // If the list row has no saved config, try loading the full record.
          if (
              Object.keys(parseRecordInfo(editRecord)).length === 0 &&
              typeof AgentService.getConnectedAppById === 'function'
          ) {
            const res = await AgentService.getConnectedAppById(editRecord.provider, editRecord.id);
            record = { ...editRecord, ...(res?.data ?? res ?? {}) };
          }
          applyFormState(buildFormDataFromConnectedApp(record, configSchema));
        } catch (e) {
          applyFormState(buildFormDataFromConnectedApp(editRecord, configSchema));
          if (!cancelled) setSaveError('Could not load the saved configuration');
        } finally {
          if (!cancelled) setIsLoadingEdit(false);
        }
      })();
    } else {
      setIsLoadingEdit(false);
      applyFormState(buildEmptyFormDataFromSchema(configSchema));
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSchema, editRecord?.id]);

  // Reset tabs when schema changes
  useEffect(() => {
    const fields = Array.isArray(configSchema?.fields) ? configSchema.fields : [];
    const hasQueryParams = fields.some((f) => f?.name === 'queryParams' && f?.type === 'keyvalue');
    setActiveAdvancedTab(hasQueryParams ? 'params' : 'headers');
  }, [configSchema]);

  // Add spinner animation to document head
  useEffect(() => {
    const styleId = 'agent-config-modal-spinner';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .agent-config-spinner {
          animation: spin 1s linear infinite;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);

  // Auto-save draft indicator
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.keys(formData).length > 0) {
        clearTimeout(draftSavedFadeTimerRef.current);
        clearTimeout(draftSavedHideTimerRef.current);

        // TODO: Save draft to localStorage or backend
        setIsDraftSaved(true);
        requestAnimationFrame(() => setShowDraftSaved(true));

        draftSavedFadeTimerRef.current = setTimeout(() => {
          setShowDraftSaved(false);
        }, 2500);
        draftSavedHideTimerRef.current = setTimeout(() => {
          setIsDraftSaved(false);
        }, 2900);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(draftSavedFadeTimerRef.current);
      clearTimeout(draftSavedHideTimerRef.current);
    };
  }, []);

  const validateField = useCallback((field, value) => {
    if (!field) return '';

    // Payload field validation
    if (field.type === 'payload') {
      const mode = payloadModes[field.name] || 'json';
      if (mode === 'json') {
        const trimmedValue = (typeof value === 'string' && value) ? value.trim() : '';
        if (field.required && !trimmedValue) {
          return `${field.label} is required`;
        }
        if (trimmedValue) {
          try {
            JSON.parse(trimmedValue);
          } catch (e) {
            return 'Invalid JSON format';
          }
        }
      } else {
        return validateKeyValueField(field, value);
      }
      return '';
    }

    // Key-value field validation
    if (field.type === 'keyvalue') {
      return validateKeyValueField(field, value);
    }

    const trimmedValue = (typeof value === 'string' && value) ? value.trim() : '';

    // In edit mode a blank password means "keep the saved one"
    if (isEditMode && field.type === 'password' && !trimmedValue) {
      return '';
    }

    if (field.required && !trimmedValue) {
      return `${field.label} is required`;
    }

    if (field.name === 'name' && trimmedValue) {
      if (trimmedValue.length < 3) {
        return 'Connection name must be at least 3 characters';
      } else if (trimmedValue.length > 100) {
        return 'Connection name must be less than 100 characters';
      } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(trimmedValue)) {
        return 'Connection name can only contain letters, numbers, spaces, hyphens, and underscores';
      }
    }

    return '';
  }, [payloadModes, isEditMode]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => {
      // REST API style sync: Endpoint -> Query Params
      if (fieldName === 'endpoint' && !suppressEndpointParamSyncRef.current) {
        const { queryString } = splitUrlAndQuery(value);
        const parsedParams = queryParamsArrayFromQueryString(queryString);
        return {
          ...prev,
          endpoint: value,
          queryParams: parsedParams.length > 0 ? parsedParams : [{ key: '', value: '' }]
        };
      }

      // Query Params -> Endpoint
      if (fieldName === 'queryParams' && !suppressEndpointParamSyncRef.current) {
        const currentEndpoint = String(prev.endpoint || '');
        const { baseUrl } = splitUrlAndQuery(currentEndpoint);
        const nextEndpoint = buildUrlFromBaseAndParams(baseUrl, value);
        suppressEndpointParamSyncRef.current = true;
        setTimeout(() => {
          suppressEndpointParamSyncRef.current = false;
        }, 0);
        return {
          ...prev,
          queryParams: value,
          endpoint: nextEndpoint
        };
      }

      // Generic dependent-field handling (showWhen)
      const nextValues = { ...prev, [fieldName]: value };
      const schemaFields = Array.isArray(configSchema?.fields) ? configSchema.fields : [];
      const hasDependents = schemaFields.some((f) => f?.showWhen?.field === fieldName);
      if (hasDependents) {
        return clearNowHiddenDependents(fieldName, nextValues, schemaFields);
      }

      return nextValues;
    });

    if (saveError) {
      setSaveError(null);
    }

    // Validate field in real-time
    if (configSchema && configSchema.fields) {
      const field = configSchema.fields.find(f => f.name === fieldName);
      if (field) {
        const error = validateField(field, value);
        setErrors(prev => {
          const newErrors = { ...prev };
          if (error) {
            newErrors[fieldName] = error;
          } else {
            delete newErrors[fieldName];
          }
          return newErrors;
        });
      }
    }
  }, [configSchema, validateField, saveError]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (configSchema && configSchema.fields) {
      configSchema.fields.forEach(field => {
        // Skip validation for fields currently hidden by showWhen
        if (!isFieldVisible(field, formData)) return;

        let value;
        if (field.type === 'keyvalue') {
          value = formData[field.name] || [];
        } else if (field.type === 'payload') {
          value = formData[field.name];
        } else {
          value = formData[field.name] || '';
        }
        const error = validateField(field, value);
        if (error) {
          newErrors[field.name] = error;
        }
      });
    }
    setErrors(newErrors);
    const ok = Object.keys(newErrors).length === 0;
    if (!ok) {
      console.warn('[AgentConfigModal] Validation failed:', newErrors, { formData });
    }
    return ok;
  }, [formData, configSchema, validateField]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setSaveError(null);
    try {
      const providerKeyForApi =
          String(provider || '').toLowerCase() === 'open_api'
              ? 'open_api'
              : provider;

      const nameField = configSchema?.fields?.find(
          field => field.name === 'name'
      );
      const name = nameField ? formData.name : null;

      const isOAuthProvider =
          Boolean(configSchema?.oauth) ||
          OAUTH_PROVIDERS.includes(provider) ||
          provider?.includes('google');

      if (isOAuthProvider) {
        const redirectUri = window.location.origin + '/apps/oauth2-redirect';

        const filteredData = Object.entries(formData || {})
            .filter(
                ([key, value]) =>
                    value !== undefined &&
                    value !== null &&
                    key !== 'name'
            )
            .map(([key, value]) => ({
              key,
              value: String(value)
            }));

        const response = await AgentService.generateOAuth(
            providerKeyForApi,
            name?.trim(),
            redirectUri,
            filteredData
        );

        if (response?.status === 'SUCCESS' && response.authUrl) {
          const width = 500;
          const height = 600;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          window.open(
              response.authUrl,
              'oauth_popup',
              `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          if (onSave) {
            onSave({
              ...formData,
              provider,
              authUrl: response.authUrl,
              isEdit: isEditMode
            });
          }
        } else {
          setSaveError(
              response?.message ||
              response?.errorMessage ||
              'Failed to generate OAuth URL'
          );
        }
      } else {
        const transformedInfo = { ...formData };

        (configSchema?.fields || []).forEach(field => {
          if (field.type === 'keyvalue') {
            if (Array.isArray(transformedInfo[field.name])) {
              transformedInfo[field.name] = keyValueRowsToObject(transformedInfo[field.name]);
            }
          } else if (field.type === 'payload') {
            const mode = payloadModes[field.name] || 'json';
            if (mode === 'keyvalue' && Array.isArray(transformedInfo[field.name])) {
              transformedInfo[field.name] = keyValueRowsToObject(transformedInfo[field.name]);
            }
          }
        });

        const providerForPayload =
            editRecord?.provider ||
            providerKeyForApi?.toUpperCase() ||
            providerKeyForApi;

        const configData = {
          id: editRecord?.id,
          name: name?.trim(),
          provider: String(providerForPayload).toUpperCase(),
          info: transformedInfo,
          additionalParams: []
        };

        const providerForPath = String(
            editRecord?.provider || providerKeyForApi || ''
        ).toUpperCase();

        const response = isEditMode
            ? await AgentService.updateOAuth(providerForPath, editRecord.id, configData)
            : await AgentService.genrateNonOauth(providerKeyForApi, configData);

        if (response?.status === API_RESPONSE.SUCCESS) {
          onSave?.({
            ...formData,
            provider: providerKeyForApi,
            id: editRecord?.id,
            isEdit: isEditMode
          });
        } else {
          setSaveError(
              response?.message ||
              response?.errorMessage ||
              (isEditMode
                  ? 'Failed to update configuration'
                  : 'Failed to generate configuration')
          );
        }
      }
    } catch (error) {
      console.error('Error saving configuration:', error);

      setSaveError(
          error?.response?.data?.message ||
          error?.errorMessage ||
          error?.message ||
          'Failed to connect. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    formData,
    validateForm,
    configSchema,
    provider,
    onSave,
    payloadModes,
    editRecord,
    isEditMode
  ]);

  const canSubmit = useMemo(() => {
    const fields = Array.isArray(configSchema?.fields) ? configSchema.fields : [];
    const required = fields.filter(
        (f) =>
            f?.required === true &&
            isFieldVisible(f, formData) &&
            // blank password in edit mode = keep saved value
            !(isEditMode && f.type === 'password')
    );
    if (!required.length) return true;
    return required.every((f) => {
      const v = formData?.[f.name];
      if (f.type === 'keyvalue') return hasAnyKeyValueData(v);
      if (f.type === 'payload') {
        const mode = payloadModes?.[f.name] || 'json';
        if (mode === 'keyvalue') return hasAnyKeyValueData(v);
        return typeof v === 'string' ? v.trim().length > 0 : false;
      }
      return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
    });
  }, [configSchema, formData, payloadModes, isEditMode]);

  const renderField = useCallback((field) => {
    let value;
    if (field.type === 'keyvalue' || field.type === 'payload') {
      value = formData[field.name] || {};
    } else {
      value = formData[field.name] || '';
    }
    const error = errors[field.name];
    const hasError = !!error;

    const requiredMark = field.required && (
        <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
    );
    const errorNode = hasError && <p style={errorTextStyle}>{error}</p>;
    const helpNode = field.helpText && <p style={helpTextStyle}>{field.helpText}</p>;

    const inputBorder = `1px solid ${hasError ? '#ef4444' : '#d1d5db'}`;

    switch (field.type) {
      case 'text':
      case 'textbox':
        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                {field.label}
                {requiredMark}
              </label>
              <input
                  type="text"
                  value={value}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: inputBorder,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db'}
              />
              {errorNode}
              {helpNode}
            </div>
        );

      case 'password': {
        const isVisible = !!visiblePasswords[field.name];
        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                {field.label}
                {requiredMark}
              </label>

              <div style={{ position: 'relative' }}>
                <input
                    type={isVisible ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={value}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={
                        field.placeholder ||
                        (isEditMode ? 'Leave blank to keep current value' : `Enter ${field.label.toLowerCase()}`)
                    }
                    style={{
                      width: '100%',
                      padding: '10px 44px 10px 12px',
                      border: inputBorder,
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db'}
                />
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() =>
                        setVisiblePasswords((prev) => ({ ...prev, [field.name]: !prev[field.name] }))
                    }
                    aria-label={isVisible ? 'Hide password' : 'Show password'}
                    title={isVisible ? 'Hide' : 'Show'}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '6px',
                      fontSize: '16px',
                      lineHeight: 1
                    }}
                >
                  <i className={`fa ${isVisible ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>

              {errorNode}
              {helpNode}
            </div>
        );
      }

      case 'select':
      case 'dropdown':
        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                {field.label}
                {requiredMark}
              </label>
              <select
                  value={value}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: inputBorder,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer'
                  }}
              >
                <option value="">{field.placeholder || `Select ${field.label.toLowerCase()}`}</option>
                {field.options && field.options.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                ))}
              </select>
              {errorNode}
            </div>
        );

      case 'textarea':
        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                {field.label}
                {requiredMark}
              </label>
              <textarea
                  value={value}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                  rows={field.rows || 4}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: inputBorder,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
              />
              {errorNode}
            </div>
        );

      case 'keyvalue': {
        const keyValuePairs = Array.isArray(value) ? value : [];
        const isTableLayout = field?.layout === 'table';
        const isHeaders = field.name === 'headers';
        const keySuggestionsFromSchema = Array.isArray(field.keyOptions) ? field.keyOptions : [];
        const effectiveKeySuggestions = Array.from(
            new Set([
              ...(isHeaders ? DEFAULT_HEADER_KEY_SUGGESTIONS : []),
              ...keySuggestionsFromSchema,
              ...keyValuePairs.map((p) => (p?.key || '').trim()).filter(Boolean)
            ])
        ).sort((a, b) => a.localeCompare(b));
        const valueOptionsByKeyFromSchema =
            field.valueOptionsByKey && typeof field.valueOptionsByKey === 'object'
                ? field.valueOptionsByKey
                : {};
        const getValueSuggestionsForKey = (headerKey) => {
          if (!isHeaders) return [];
          const normalized = normalizeHeaderKeyForLookup(headerKey);
          const base = DEFAULT_HEADER_VALUE_SUGGESTIONS_BY_KEY[normalized] || [];
          const fromSchemaRaw = valueOptionsByKeyFromSchema[normalized];
          const fromSchema = Array.isArray(fromSchemaRaw) ? fromSchemaRaw : [];
          return Array.from(new Set([...base, ...fromSchema]));
        };

        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                {field.label}
                {requiredMark}
              </label>
              <KeyValueBox
                  fieldName={field.name}
                  hasError={hasError}
                  pairs={keyValuePairs}
                  onChange={(next) => handleFieldChange(field.name, next)}
                  tableLayout={isTableLayout}
                  autocomplete={isHeaders}
                  keySuggestions={effectiveKeySuggestions}
                  getValueSuggestions={getValueSuggestionsForKey}
              />
              {errorNode}
              {helpNode}
            </div>
        );
      }

      case 'payload': {
        const payloadMode = payloadModes[field.name] || 'json';
        const payloadValue = payloadMode === 'json'
            ? (typeof value === 'string' ? value : '')
            : (Array.isArray(value) ? value : []);

        const modeButtonStyle = (active) => ({
          padding: '6px 12px',
          border: `1px solid ${active ? '#3b82f6' : '#d1d5db'}`,
          backgroundColor: active ? '#3b82f6' : '#ffffff',
          color: active ? '#ffffff' : '#374151',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 500
        });

        return (
            <div key={field.name} style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                  {field.label}
                  {requiredMark}
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                      type="button"
                      onClick={() => {
                        setPayloadModes(prev => ({ ...prev, [field.name]: 'json' }));
                        // keyvalue -> json
                        if (payloadMode === 'keyvalue' && Array.isArray(value) && value.length > 0) {
                          const jsonObj = {};
                          value.forEach(pair => {
                            if (pair.key) {
                              jsonObj[pair.key] = pair.value || '';
                            }
                          });
                          handleFieldChange(field.name, JSON.stringify(jsonObj, null, 2));
                        } else if (payloadMode === 'keyvalue') {
                          handleFieldChange(field.name, '');
                        }
                      }}
                      style={modeButtonStyle(payloadMode === 'json')}
                  >
                    Raw JSON
                  </button>
                  <button
                      type="button"
                      onClick={() => {
                        setPayloadModes(prev => ({ ...prev, [field.name]: 'keyvalue' }));
                        // json -> keyvalue
                        if (payloadMode === 'json' && typeof value === 'string' && value.trim()) {
                          try {
                            const jsonObj = JSON.parse(value);
                            const pairs = Object.entries(jsonObj).map(([key, val]) => ({
                              key,
                              value: typeof val === 'string' ? val : JSON.stringify(val)
                            }));
                            handleFieldChange(field.name, pairs.length > 0 ? pairs : [{ key: '', value: '' }]);
                          } catch (e) {
                            handleFieldChange(field.name, [{ key: '', value: '' }]);
                          }
                        } else if (payloadMode === 'json') {
                          handleFieldChange(field.name, [{ key: '', value: '' }]);
                        }
                      }}
                      style={modeButtonStyle(payloadMode === 'keyvalue')}
                  >
                    Key-Value Pair
                  </button>
                </div>
              </div>

              {payloadMode === 'json' ? (
                  <textarea
                      value={payloadValue}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder || '{\n  "key": "value"\n}'}
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: inputBorder,
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'monospace',
                        backgroundColor: '#f9fafb'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = hasError ? '#ef4444' : '#d1d5db'}
                  />
              ) : (
                  <KeyValueBox
                      fieldName={field.name}
                      hasError={hasError}
                      pairs={payloadValue}
                      onChange={(next) => handleFieldChange(field.name, next)}
                      tableLayout
                  />
              )}

              {errorNode}
              {helpNode}
            </div>
        );
      }

      default:
        return null;
    }
  }, [formData, errors, handleFieldChange, payloadModes, visiblePasswords, isEditMode]);

  if (!configSchema) {
    return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p>No configuration schema available for {agentName}</p>
          <button onClick={onClose}>Go Back</button>
        </div>
    );
  }

  const busy = isLoading || isLoadingEdit || !canSubmit;

  return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Header */}
          <div style={{
            padding: '10px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <button
                    className="back-button"
                    aria-label={isEditMode ? 'Back to Connected Apps' : 'Back to Agent Apps'}
                    type="button"
                    onClick={onClose}
                >
                  <i className="fa fa-long-arrow-left" />
                </button>
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#1e293b',
                  margin: 0
                }}>
                  {isEditMode ? `Edit ${editRecord.name || agentName}` : `Configure ${agentName}`}
                </h2>
              </div>
              <p style={{
                fontSize: '14px',
                color: '#64748b',
                margin: '4px 0 0 52px'
              }}>
                {isEditMode
                    ? 'Update your connected app configuration'
                    : (configSchema.description || 'Follow the guide to set up your connection')}
              </p>
            </div>
            {isDraftSaved && (
                <span style={{
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: showDraftSaved ? 1 : 0,
                  transition: 'opacity 400ms ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i className="fa fa-check" />
                  Draft saved
                </span>
            )}
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            padding: '24px',
            overflowY: 'auto'
          }}>
            {isLoadingEdit ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#64748b' }}>
                  <span
                      style={{ ...spinnerStyle, border: '2px solid #64748b', borderTop: '2px solid transparent' }}
                      className="agent-config-spinner"
                  />
                  <p style={{ marginTop: '12px', marginBottom: 0 }}>Loading configuration…</p>
                </div>
            ) : null}
            {saveError && (
                <div style={{
                  padding: '12px 16px',
                  marginBottom: '20px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '14px'
                }}>
                  {saveError}
                </div>
            )}
            <div style={{ display: isLoadingEdit ? 'none' : 'block' }}>
              {(() => {
                const fields = Array.isArray(configSchema.fields) ? configSchema.fields : [];
                const methodField = fields.find((f) => f && f.name === 'method' && (f.type === 'select' || f.type === 'dropdown'));
                const endpointField = fields.find((f) => f && f.name === 'endpoint' && (f.type === 'text' || f.type === 'textbox'));
                const queryParamsField = fields.find(
                    (f) => f && f.name === 'queryParams' && f.type === 'keyvalue'
                );
                const headersField = fields.find(
                    (f) => f && f.name === 'headers' && f.type === 'keyvalue'
                );
                const payloadField = fields.find((f) => f && f.type === 'payload');

                // REST API / Postman-style layout:
                // top row: Method + Endpoint, then tabs: Params / Headers / Payload
                if (methodField && endpointField && headersField && payloadField) {
                  const nonTabbed = fields.filter(
                      (f) =>
                          f !== methodField &&
                          f !== endpointField &&
                          f !== queryParamsField &&
                          f !== headersField &&
                          f !== payloadField
                  );

                  const paramsFilled = hasAnyKeyValueData(formData.queryParams);
                  const headersFilled = hasAnyKeyValueData(formData.headers);
                  const payloadMode = payloadModes[payloadField.name] || 'json';
                  const payloadRaw = formData[payloadField.name];
                  const payloadFilled =
                      payloadMode === 'json'
                          ? Boolean(String(payloadRaw || '').trim())
                          : hasAnyKeyValueData(payloadRaw);
                  const filledByTabId = {
                    params: paramsFilled,
                    headers: headersFilled,
                    payload: payloadFilled
                  };
                  const tabs = [
                    ...(queryParamsField ? [{ id: 'params', label: 'Params' }] : []),
                    { id: 'headers', label: 'Headers' },
                    { id: 'payload', label: 'Payload' }
                  ];

                  return (
                      <>
                        {nonTabbed
                            .filter((field) => isFieldVisible(field, formData))
                            .map((field) => renderField(field))}

                        <div style={{ marginTop: '8px', marginBottom: '18px' }}>
                          <label style={labelStyle}>
                            {endpointField.label}
                            {endpointField.required && <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>}
                          </label>

                          <div style={{
                            display: 'flex',
                            width: '100%',
                            border: `2px solid ${errors[methodField.name] || errors[endpointField.name] ? '#ef4444' : '#60a5fa'}`,
                            borderRadius: '6px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff'
                          }}>
                            <select
                                value={formData[methodField.name] || ''}
                                onChange={(e) => handleFieldChange(methodField.name, e.target.value)}
                                style={{
                                  width: '110px',
                                  padding: '10px 12px',
                                  border: 'none',
                                  outline: 'none',
                                  backgroundColor: '#ffffff',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 700,
                                  color: '#111827',
                                  borderRight: '1px solid #e5e7eb'
                                }}
                            >
                              <option value="" disabled>
                                {methodField.placeholder || 'Method'}
                              </option>
                              {methodField.options && methodField.options.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                              ))}
                            </select>

                            <input
                                type="text"
                                value={formData[endpointField.name] || ''}
                                onChange={(e) => handleFieldChange(endpointField.name, e.target.value)}
                                placeholder={endpointField.placeholder || 'Enter endpoint'}
                                style={{
                                  flex: 1,
                                  padding: '10px 12px',
                                  border: 'none',
                                  outline: 'none',
                                  fontSize: '14px',
                                  backgroundColor: '#ffffff'
                                }}
                            />
                          </div>

                          {(errors[methodField.name] || errors[endpointField.name]) && (
                              <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                                {errors[endpointField.name] || errors[methodField.name]}
                              </p>
                          )}
                          {endpointField.helpText && (
                              <p style={helpTextStyle}>{endpointField.helpText}</p>
                          )}
                        </div>

                        <div style={{ marginTop: '6px' }}>
                          <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            borderBottom: '1px solid #e5e7eb',
                            marginBottom: '16px'
                          }}>
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveAdvancedTab(tab.id)}
                                    style={{
                                      padding: '10px 12px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: activeAdvancedTab === tab.id ? 700 : 500,
                                      color: activeAdvancedTab === tab.id ? '#111827' : '#6b7280',
                                      borderBottom: activeAdvancedTab === tab.id ? '2px solid var(--activemenu, #3d91e4)' : '2px solid transparent',
                                      marginBottom: '-1px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}
                                >
                                  <span>{tab.label}</span>
                                  {filledByTabId[tab.id] && (
                                      <span
                                          style={{
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '999px',
                                            backgroundColor: '#10b981',
                                            display: 'inline-block'
                                          }}
                                          aria-label={`${tab.label} has values`}
                                      />
                                  )}
                                </button>
                            ))}
                          </div>

                          {activeAdvancedTab === 'params' && queryParamsField && renderField(queryParamsField)}
                          {activeAdvancedTab === 'headers' && renderField(headersField)}
                          {activeAdvancedTab === 'payload' && renderField(payloadField)}
                        </div>
                      </>
                  );
                }

                // Default: render all fields as-is, skipping any hidden by showWhen.
                return fields
                    .filter((field) => isFieldVisible(field, formData))
                    .map((field) => renderField(field));
              })()}
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500
                  }}
              >
                Close
              </button>
            </div>
            <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: busy ? '#d1d5db' : '#10b981',
                  color: '#ffffff',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  opacity: busy ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
            >
              {isLoading ? (
                  <>
                    <span style={spinnerStyle} className="agent-config-spinner"></span>
                    {isEditMode ? 'Saving…' : 'Connecting...'}
                  </>
              ) : (
                  isEditMode ? 'Save changes' : 'Save Configuration'
              )}
            </button>
          </div>
        </div>
      </div>
  );
};

AgentConfigModal.propTypes = {
  provider: PropTypes.string.isRequired,
  agentName: PropTypes.string.isRequired,
  configSchema: PropTypes.shape({
    description: PropTypes.string,
    oauth: PropTypes.bool,
    fields: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'textbox', 'password', 'select', 'dropdown', 'textarea', 'keyvalue', 'payload']).isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      required: PropTypes.bool,
      defaultValue: PropTypes.any,
      helpText: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }))
    }))
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  editRecord: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    provider: PropTypes.string
  })
};

export default AgentConfigModal;